import React, { useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import Select from "react-select";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { showError, showSuccess } from "toast/toast";
import { Group, PaymentMethod } from "types";
import { UIInput, UIReactSelect, UIReactMultiSelect } from "components/Inputfields";
import { getAuthCookie } from "../utils/authCookies";


const INPUT_BG = "#f5f6f8";
const BORDER = "#e5e7eb";
export default function SignageAddEditPaymentMethod() {
    const { methodId } = useParams<{ methodId: string }>();
    const isEdit = Boolean(methodId);
    const navigate = useNavigate();
    const location = useLocation();
    const isSignageTeammember = location.pathname.includes('/signageteammember');
    const baseRoute = isSignageTeammember ? '/signageteammember' : '/signage';
    const token = getAuthCookie("sessionToken") || "";

    const [groups, setGroups] = useState<Group[]>([]);
    const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [formSubmitting, setFormSubmitting] = useState<boolean>(false);

    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const resp = await fetch(`${process.env.REACT_APP_API_URL}/customer-groups`, {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                });
                const data = await resp.json();
                if (!resp.ok) throw new Error(data?.message || "Failed to fetch groups");
                if (mounted) setGroups(Array.isArray(data) ? data : []);
            } catch (e) {
                console.error(e);
                showError("Failed to load groups.");
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => { mounted = false; };
    }, [token]);

    // Load existing record in edit mode
    useEffect(() => {
        if (!isEdit) return;
        let mounted = true;
        (async () => {
            try {
                const resp = await fetch(`${process.env.REACT_APP_API_URL}/payment-methods/${methodId}`, {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                });
                const data = await resp.json();
                if (!resp.ok) throw new Error(data?.message || "Failed to fetch payment method");
                if (mounted) setPaymentMethod(data);
            } catch (e) {
                console.error(e);
                showError("Failed to load payment method.");
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => { mounted = false; };
    }, [isEdit, methodId, token]);

    const groupOptions = useMemo(
        () => (groups || []).map((g: any) => ({ label: g.name, value: g._id || g.id })),
        [groups]
    );

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            name: paymentMethod?.name || "",
            description: paymentMethod?.description || "",
            is_credit: paymentMethod?.is_credit ?? false,
            number_of_days: paymentMethod?.number_of_days ?? 0,
            group_ids: (paymentMethod?.group_ids as unknown as string[]) || [],
            statuss: paymentMethod ? (paymentMethod.status ? "Active" : "Inactive") : "Active",
        },
        validationSchema: Yup.object({
            name: Yup.string()
                .min(2, "paymentMethods name must be at least 2 characters long")
                // .max(50, "paymentMethods name cannot be longer than 50 characters")
                .required("paymentMethods Name is required"),
            // statuss: Yup.string().required("Status is required"),
        }),
        onSubmit: async (values) => {
            setFormSubmitting(true);
            try {
                const payload = {
                    name: values.name,
                    description: values.description || "",
                    is_credit: !!values.is_credit,
                    number_of_days: values.is_credit ? (values.number_of_days || 0) : 0,
                    group_ids: values.group_ids || [],
                    status: values.statuss === "Active",
                };

                const url = isEdit
                    ? `${process.env.REACT_APP_API_URL}/payment-methods/${methodId}`
                    : `${process.env.REACT_APP_API_URL}/payment-methods`;
                const method = isEdit ? "PUT" : "POST";

                const resp = await fetch(url, {
                    method,
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify(payload),
                });
                const data = await resp.json().catch(() => ({}));
                if (!resp.ok) throw new Error(data?.message || `Failed to ${isEdit ? "update" : "create"}`);

                showSuccess(`paymentMethods ${isEdit ? "updated" : "added"} successfully!`);
                navigate(`${baseRoute}/paymentMethods`);
            } catch (err) {
                console.error(err);
                showError(`Failed to ${isEdit ? "update" : "add"} paymentMethods.`);
            } finally {
                setFormSubmitting(false);
            }
        },
    });

    // Keep the SAME UI and classes from the original Add screen.
    // Only alignment tweaks; no class removals.
    return (
        <div className="content container">
            <div className="row">
                <div className="col-md-12 d-flex justify-content-center">
                    <div className="col-md-9">
                        <div className="mb-5">
                            <div className="mt-1 mb-3">
                                <h3>
                                    {isEdit ? "Edit Payment Method" : "Add Payment Method"}
                                </h3>
                            </div>
                        </div>

                        {/* card */}
                        <div
                            className="box-shadow shadow-border-3 mb-5"
                        >
                            {/* header */}
                            <div className="d-flex align-items-center ms-4" style={{ padding: "33px 18px", borderBottom: "1px solid #eef2f7" }}>
                                <span className="d-inline-flex align-items-center justify-content-center" style={{ width: 26, height: 26 }}>
                                    <i className="bi bi-credit-card-2-front" style={{ fontSize: 26, color: "#155dfc", lineHeight: 1 }} />
                                </span>
                                <div className="fs-19 roboto-new ms-2">Payment Method Details</div>
                            </div>

                            {/* body */}
                            <form onSubmit={formik.handleSubmit} className="mx-4 mb-4">
                                <div style={{ padding: "54px 18px 0px 18px" }}>
                                    <div className="ms-1 mb-2 fs-19 roboto-new">Payment Method Information</div>
                                    <hr />

                                    <div className="row ms-1" id="row-overlap">
                                        <div className="col-lg-6 mb-2 roboto-new" id="row-overlap">
                                            <UIInput
                                                formik={formik}
                                                name="name"
                                                label="Name"
                                                required
                                                placeholder="Enter payment method name"
                                            />
                                        </div>
                                        <div className="col-lg-6 roboto-new" id="row-overlap">
                                            <UIReactMultiSelect
                                                formik={formik}
                                                name="group_ids"
                                                label="Groups"
                                                placeholder="Select groups"
                                                options={groupOptions}
                                            />
                                        </div>
                                        <div className="row mb-2 mt-2">
                                            <div className="col-lg-6 d-flex align-items-center mb-3 roboto-new" id="row-overlap">
                                                <div className="d-flex align-items-center" style={{ gap: 28 }}>
                                                    <div className="form-check d-flex align-items-center gap-2">
                                                        <input
                                                            id="is_credit"
                                                            name="is_credit"
                                                            type="checkbox"
                                                            className="form-check-input square-checkbox"
                                                            checked={!!formik.values.is_credit}
                                                            onChange={(e) => formik.setFieldValue("is_credit", e.currentTarget.checked)}
                                                            onBlur={formik.handleBlur}
                                                        />
                                                        <label htmlFor="is_credit" className="form-check-label">Credit</label>
                                                    </div>
                                                </div>
                                            </div>
                                            {formik.values.is_credit && (
                                                <div className="col-lg-6 roboto-new mb-3" id="row-overlap">
                                                    <label className="form-label">Number of Credit Days</label>
                                                    <input
                                                        type="number"
                                                        id="number_of_days"
                                                        name="number_of_days"
                                                        className="form-control br-8"
                                                        value={formik.values.number_of_days === 0 ? "" : formik.values.number_of_days}
                                                        onChange={(e) => {
                                                            const value = e.target.value;
                                                            if (value === "") {
                                                                formik.setFieldValue("number_of_days", 0);
                                                            } else {
                                                                const numValue = parseInt(value, 10);
                                                                if (!isNaN(numValue)) {
                                                                    formik.setFieldValue("number_of_days", numValue);
                                                                }
                                                            }
                                                        }}
                                                        onBlur={formik.handleBlur}
                                                        placeholder="0"
                                                        style={{
                                                            borderRadius: 10,
                                                            background: INPUT_BG,
                                                            // borderColor: BORDER,
                                                            fontSize: 14,
                                                        }}
                                                    />
                                                </div>
                                            )}
                                        </div>

                                        {/* Description */}
                                        <div className="col-lg-12 roboto-new" id="row-overlap">
                                            <label className="form-label">Description</label>
                                            <textarea
                                                id="description"
                                                name="description"
                                                className="form-control"
                                                rows={4}
                                                value={formik.values.description}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                style={{
                                                    borderRadius: 10,
                                                    background: INPUT_BG,
                                                    borderColor: BORDER,
                                                    fontSize: 14,
                                                }}
                                                placeholder="Enter shipping description (optional)"
                                            />
                                        </div>

                                        {/* Status + checkboxes row */}
                                        <div className="col-12 ms-lg-3" id="row-overlap" >
                                            <div className="row align-items-center mt-4">
                                                {/* Status (left) */}
                                                <div className="col-lg-3 roboto-new mb-3" id="row-overlap">
                                                    <UIReactSelect
                                                        formik={formik}
                                                        name="statuss"
                                                        label="Status"
                                                        options={[
                                                            { value: "Active", label: "Active" },
                                                            { value: "Inactive", label: "Inactive" },
                                                        ]}
                                                        placeholder="Select"
                                                    />
                                                </div>
                                                {/* Checkboxes (right) */}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-12 mb-2" id="row-overlap">
                                    <hr className="ms-4 me-3" />
                                </div>

                                {/* footer */}
                                <div className="d-flex justify-content-between align-items-center ms-1" style={{ padding: "14px 18px" }}>
                                    <button
                                        type="button"
                                        onClick={() => navigate(-1)}
                                        className="btn back-btn surface-btn fw-bold fs-16"
                                    >
                                        <i className="bi bi-chevron-left me-2" />
                                        Back
                                    </button>

                                    <button
                                        type="submit"
                                        className="btn btn-primary save-btn surface-btn fw-bold fs-16"
                                        disabled={formSubmitting}
                                    >
                                        {formSubmitting ? "Submitting..." : "Save"}
                                    </button>
                                </div>
                            </form>
                        </div>
                        {/* /card */}
                    </div>
                </div>
            </div>
            <style>{`
                #number_of_days.form-control:hover {
                    border-color: #9ca3af;
                    background-color: #fafafa;
                }
                #number_of_days.form-control:focus {
                    border-color: #9ca3af;
                    background-color: #fafafa;
                    outline: none;
                    box-shadow: 0 0 0 0.2rem rgba(156, 163, 175, 0.25);
                }
            `}</style>
        </div>
    );
}